import { Clock, Hash, Sparkles, Users } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useSolution } from "@/hooks/use-solutions";
import { ProcessError, useProcessSource, useSource } from "@/hooks/use-sources";
import type { Stakeholder } from "@/hooks/use-stakeholders";
import { formatDate } from "@/lib/format";
import { ExtractedItemsPreview } from "./extracted-items-preview";
import { ParticipantStack } from "./participant-stack";
import { ProcessSourceConfirmDialog } from "./process-source-confirm-dialog";
import { ProcessSourceProgressDialog } from "./process-source-progress-dialog";
import { SourceIcon, sourceTypeLabel } from "./source-icon";

interface SourceDetailSheetProps {
  sourceId: string | null;
  solutionId: string;
  stakeholders: Stakeholder[] | undefined;
  onOpenChange: (open: boolean) => void;
}

export function SourceDetailSheet({
  sourceId,
  solutionId,
  stakeholders,
  onOpenChange,
}: SourceDetailSheetProps) {
  const isOpen = sourceId !== null;
  const { data, isLoading } = useSource(sourceId);
  const processMutation = useProcessSource(solutionId);
  const solution = useSolution(solutionId);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const hasBeenProcessed = data?.processedAt != null;
  const isProcessing = processMutation.isPending;
  const baselineConfirmed = Boolean(solution.data?.baselineConfirmedAt);
  const processDisabled = isProcessing || !baselineConfirmed;

  const [progressOpen, setProgressOpen] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);

  const startProcessing = async (reprocess: boolean) => {
    if (!sourceId) return;
    setRunId(null);
    setProgressOpen(true);
    try {
      await processMutation.mutateAsync({ sourceId, reprocess, onRunStarted: setRunId });
    } catch (err) {
      if (err instanceof ProcessError && err.code === "REPROCESS_CONFIRMATION_REQUIRED") {
        setConfirmOpen(true);
      }
      // All other errors are toasted by the mutation's onError.
    } finally {
      setProgressOpen(false);
    }
  };

  const handleProcessClick = () => startProcessing(false);

  const handleConfirmReprocess = async () => {
    setConfirmOpen(false);
    await startProcessing(true);
  };

  const handleProgressCancel = () => {
    // Close immediately; the cancel mutation fires inside the dialog and the
    // already-running mutation will settle via its CANCELLED path.
    setProgressOpen(false);
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-[640px] overflow-y-auto">
          {isLoading && (
            <div className="p-6 space-y-3">
              <Skeleton className="h-5 w-1/3" />
              <Skeleton className="h-7 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-32 w-full mt-4" />
            </div>
          )}

          {data && (
            <>
              <SheetHeader className="border-b border-[var(--color-border)]">
                <div className="flex items-start gap-3">
                  <SourceIcon type={data.type} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                      {sourceTypeLabel(data.type)}
                    </div>
                    <SheetTitle className="font-display text-lg leading-tight mt-0.5">
                      {data.name}
                    </SheetTitle>
                    <SheetDescription className="mt-1">
                      {formatDate(data.sourceDate)}
                      {data.durationMin != null && ` · ${data.durationMin} min`}
                    </SheetDescription>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {/* span wrapper: disabled buttons swallow pointer events,
                          so the tooltip needs a non-disabled trigger. */}
                      <span className="shrink-0 inline-block">
                        <Button
                          size="sm"
                          variant={hasBeenProcessed ? "outline" : "default"}
                          onClick={handleProcessClick}
                          disabled={processDisabled}
                          className={processDisabled ? "pointer-events-none" : undefined}
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                          {hasBeenProcessed ? "Reprocess" : "Process AI"}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {!baselineConfirmed && (
                      <TooltipContent side="bottom">
                        Confirm a baseline in the Scope tab before processing sources.
                      </TooltipContent>
                    )}
                  </Tooltip>
                </div>
              </SheetHeader>

              <div className="p-6 space-y-6">
                <DetailMetaRow icon={Users} label="Participants">
                  {data.participantStakeholderIds.length === 0 ? (
                    <span className="text-muted-foreground">None recorded</span>
                  ) : (
                    <ParticipantStack
                      participantIds={data.participantStakeholderIds}
                      stakeholders={stakeholders}
                      max={8}
                    />
                  )}
                </DetailMetaRow>

                {data.externalKey && (
                  <DetailMetaRow icon={Hash} label="External key">
                    <code className="text-xs">{data.externalKey}</code>
                  </DetailMetaRow>
                )}

                {data.durationMin != null && (
                  <DetailMetaRow icon={Clock} label="Duration">
                    {data.durationMin} min
                  </DetailMetaRow>
                )}

                {hasBeenProcessed && sourceId && (
                  <div>
                    <h3 className="font-display text-sm font-semibold mb-2">Extracted items</h3>
                    <ExtractedItemsPreview sourceId={sourceId} solutionId={solutionId} />
                  </div>
                )}

                <div>
                  <h3 className="font-display text-sm font-semibold mb-2">Transcript</h3>
                  {data.transcriptLines.length > 0 ? (
                    <ol className="space-y-2 text-sm">
                      {data.transcriptLines.map((line) => (
                        <li key={line.id} className="flex gap-3">
                          {line.tsLabel && (
                            <span className="text-xs text-muted-foreground tabular-nums shrink-0 w-16">
                              {line.tsLabel}
                            </span>
                          )}
                          <span className="text-foreground/85">{line.text}</span>
                        </li>
                      ))}
                    </ol>
                  ) : data.rawText ? (
                    <pre className="text-sm whitespace-pre-wrap font-sans text-foreground/85">
                      {data.rawText}
                    </pre>
                  ) : (
                    <div className="rounded-md border border-dashed border-[var(--color-border)] p-6 text-center text-sm text-muted-foreground">
                      No transcript on file.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <ProcessSourceConfirmDialog
        sourceId={sourceId}
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={handleConfirmReprocess}
        isProcessing={isProcessing}
      />

      <ProcessSourceProgressDialog
        open={progressOpen}
        sourceName={data?.name ?? ""}
        sourceByteLength={data?.rawText?.length ?? 0}
        runId={runId}
        onCancel={handleProgressCancel}
      />
    </>
  );
}

function DetailMetaRow({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Users;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
        <div className="mt-0.5">{children}</div>
      </div>
    </div>
  );
}
